/* eslint-disable @typescript-eslint/no-explicit-any */
import Axios, { AxiosError, AxiosInstance, AxiosPromise, AxiosRequestConfig, AxiosResponse, CancelToken, CancelTokenSource, ResponseType } from 'axios/index';
import { appStore } from '@app/Stores/AppStore';
import { AzureAuth } from '@app/Components/AzureAuth';
import { emitter } from '@app/Services/EventEmitter';
import { NotificationHandler } from '@app/Components/ToastNotification';
import { DateTimeService } from '@app/Services/DateTimeService';
import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import { modalService } from '@app/Components/Modal';

// While the database is not online the DatabaseUpdater answers API calls with this text and status 200
// (Startup sets NotReadyStatusCode = OK so the proxy does not turn it into a 502).
const DATABASE_UPDATER_NOT_READY_RESPONSE = 'Site is under maintenance';

class ApiHeaders {
    [key: string]: string | number;
}

export interface IAjaxOptions {
    responseType?: ResponseType;
    showLoader?: boolean;
    requestKey?: string;
    completion?: PromiseCompletion;
    cancellationToken?: CancelToken;
    cancelDuplicatedRequests?: boolean;
}

type AxiosMethodDefinition = (url: string, data?: any, config?: AxiosRequestConfig) => AxiosPromise;
type AxiosMethodChooser = (instance: AxiosInstance) => AxiosMethodDefinition;

export type Response = AxiosResponse;
export type ResponseError = AxiosError;
export type ResponseInterceptor = {
    response: (value: Response) => Response | Promise<Response>,
    error: (err: ResponseError) => ResponseError
};

export enum AxiosMethod {
    GET = 'GET',
    DELETE = 'DELETE',
    HEAD = 'HEAD',
    OPTIONS = 'OPTIONS',
    POST = 'POST',
    PUT = 'PUT',
    PATCH = 'PATCH'
};

export default class ApiService {
    private static _responseInterceptors: ResponseInterceptor[] = [];
    private static _cancelTokenCollection: Map<string, CancelTokenSource> = new Map();

    public static addResponseInterceptor(interceptor: ResponseInterceptor) {
        ApiService._responseInterceptors.push(interceptor);
    }

    private static _addAndChechCancelationToken(tokenKey: string, source: CancelTokenSource) {
        const duplicateToken = ApiService._cancelTokenCollection.get(tokenKey);
        if (duplicateToken) {
            duplicateToken.cancel();
        }
        ApiService._cancelTokenCollection.set(tokenKey, source);
    }

    private static _callMethod<TResponse>(methodChooser: AxiosMethodChooser, url: string, data?: unknown, options?: IAjaxOptions): AxiosPromise<TResponse> {
        const responseType = options && options.responseType || 'json';
        const showLoader = options && options.showLoader || false;

        let cancelToken = options?.cancellationToken;
        if (options?.cancelDuplicatedRequests) {
            const source = Axios.CancelToken.source();
            cancelToken = source.token;
            const requestKey = options?.requestKey ? options.requestKey : ApiService._getAbsoluteUrl(url);
            ApiService._addAndChechCancelationToken(requestKey, source);
        }

        const instance = ApiService._getInstance(responseType, options, cancelToken);
        const result = methodChooser(instance)(ApiService._getAbsoluteUrl(url), data);
        const requestTimestamp = DateTimeService.today().getTime();

        if (showLoader) {
            appStore.showAjaxSpinner(requestTimestamp);
        } else {
            appStore.showLoading();
        }

        const functionHide = (showLoader: boolean) => {
            if (showLoader) {
                appStore.hideAjaxSpinner(requestTimestamp);
            } else {
                appStore.hideLoading();
            }
        };

        options && options.completion && options.completion.subscribe(result);
        result.catch((error: AxiosError) => {
            ApiService.handleError(url, error);
        });
        result.then(() => {
            functionHide(showLoader);
        }, () => {
            functionHide(showLoader);
        });
        return result;
    }

    public static sendRequest<TResponse>(method: AxiosMethod, url: string, data?: unknown, options?: IAjaxOptions): AxiosPromise<TResponse> {
        switch (method) {
            case AxiosMethod.POST:
                return ApiService.post<TResponse>(url, data, options);
            case AxiosMethod.GET:
                return ApiService.get<TResponse>(url, data, options);
            case AxiosMethod.PUT:
                return ApiService.put<TResponse>(url, data, options);
            case AxiosMethod.DELETE:
                return ApiService.delete<TResponse>(url, data, options);
            case AxiosMethod.PATCH:
                return ApiService.patch<TResponse>(url, data, options);
            default:
                return Promise.reject();
        }
    }

    public static get<TResponse>(url: string, data?: unknown, options?: IAjaxOptions): AxiosPromise<TResponse> {
        return ApiService._callMethod<TResponse>((instance: AxiosInstance) => instance.get, url, { params: data }, options);
    }

    public static put<TResponse>(url: string, putData?: unknown, options?: IAjaxOptions): AxiosPromise<TResponse> {
        return ApiService._callMethod<TResponse>((instance: AxiosInstance) => instance.put, url, putData, options);
    }

    public static post<TResponse>(url: string, postData?: unknown, options?: IAjaxOptions): AxiosPromise<TResponse> {
        return ApiService._callMethod<TResponse>((instance: AxiosInstance) => instance.post, url, postData, options);
    }

    public static patch<TResponse>(url: string, patchData?: unknown, options?: IAjaxOptions): AxiosPromise<TResponse> {
        return ApiService._callMethod<TResponse>((instance: AxiosInstance) => instance.patch, url, patchData, options);
    }

    public static delete<TResponse>(url: string, data?: unknown, options?: IAjaxOptions): AxiosPromise<TResponse> {
        return ApiService._callMethod<TResponse>((instance: AxiosInstance) => instance.delete, url, { params: data }, options);
    }

    public static handleError(url: string, error: AxiosError) {
        if (Axios.isCancel(error)) {
            console.log(`Request canceled: ${url}\nMessage: ${error.message}`);
            return;
        }
        if (error && error.response && error.response.status === 409) {
            return;
        }
        errorHandleService.showError(error);
    }

    public static typeCheck = (el: any, isDate?: boolean) => {
        if (!el) return el;
        let typeEl = el;
        switch (typeof el) {
            case 'string':
                typeEl = ApiService.strCheck(el, isDate);
                break;
            case 'object':
                typeEl = Array.isArray(el) ? ApiService.arrCheck(el) : ApiService.objCheck(el);
                break;
        }
        return typeEl;
    };

    private static strCheck = (str: string, isDate?: boolean) => {
        if (isDate && DateTimeService.ISO_8601_date.test(str)) return DateTimeService.fromString(str);
        return str;
    };

    private static arrCheck = (array: unknown[]) => {
        return array.map((el: unknown) => {
            return ApiService.typeCheck(el);
        });
    };

    private static objCheck = (obj: Record<string, unknown>) => {
        Object.keys(obj).forEach(key => {
            obj[key] = ApiService.typeCheck(obj[key], key.indexOf('date') === 0 
            || key.indexOf('Date') !== -1
            || key.endsWith('Utc')
            || key === 'timestamp');
        });
        return obj;
    };

    private static _getInstance(responseType: ResponseType = 'json', options?: IAjaxOptions, cancelToken?: CancelToken): AxiosInstance {
        const headers = new ApiHeaders();
        if (appStore.currentToken) {
            headers['Authorization'] = 'Bearer ' + appStore.currentToken;
        }
        if (appStore.isHistorized) {
            headers['App-Is-Historized'] = 'true';
        }
        if (appStore.isDisabledChangeLog) {
            headers['App-Is-Change-Log-Disabled'] = 'true';
        }
        if (appStore.impersonationRolesHeader) {
            headers['App-Impersonation-Roles'] = appStore.impersonationRolesHeader;
        }

        const transformResponse = (data: unknown, headers: { [key: string]: string }) => {
            if (options?.responseType === 'text') return data;

            let result = data;
            if (typeof result === 'string' && headers['content-type']?.startsWith('application/json')) {
                result = JSON.parse(result);
            }

            if (typeof result === 'object') {
                result = ApiService.typeCheck(result);
            }

            return result;
        };

        const axiosConfig: AxiosRequestConfig = {
            responseType: responseType,
            headers: headers,
            transformResponse: transformResponse,
            cancelToken: cancelToken
        };

        const axiosInstance = Axios.create(axiosConfig);
        axiosInstance.interceptors.response.use((res) => {
            const contentType = res.headers['content-type'];
            if (contentType && contentType.indexOf('text/html') > -1 && responseType === 'json') {
                throw new Error('API call to ' + res.config.url + ' returned not expected content type: ' + contentType);
            }
            if (contentType?.startsWith('text/plain') && res.data === DATABASE_UPDATER_NOT_READY_RESPONSE) {
                throw new Error(DATABASE_UPDATER_NOT_READY_RESPONSE);
            }

            return res;
        }, (err) => {
            if (err.response && err.response.status === 401) {
                return new Promise(() => {
                    AzureAuth.login();
                });
            }
            if (err.response && err.response.status === 403) {
                emitter.emit('notify-error-403', err);
            }
            throw err;
        });

        ApiService._responseInterceptors.forEach(x => axiosInstance.interceptors.response.use(x.response, x.error));

        return axiosInstance;
    }

    public static getAbsoluteUrl(url: string): string {
        return ApiService._getAbsoluteUrl(url);
    }

    private static _getAbsoluteUrl(url: string): string {
        let relariveUrl = url;
        if (relariveUrl && relariveUrl[0] !== '/') relariveUrl = '/' + url;
        if (!relariveUrl.startsWith('/api')) relariveUrl = '/api' + url;
        return location.origin + relariveUrl;
    }

    public static getDynamicUrl(url: string, params?: any & {}): string {
        let _url = url;
        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                _url = _url.replace(`{${key}}`, encodeURIComponent(params[key] as string));
            }
        }
        return _url;
    }
}

class ErrorHandleService {
    private _lastError: { message: string, time: number };

    showError(error: AxiosError) {
        const currentTime = (new Date()).getTime();
        console.log('--error AJAX: ', error);
        if (!this._lastError || this._lastError.message !== error.message || (currentTime - this._lastError.time > 10000)) {
            NotificationHandler.showError(error.message, '--error AJAX');
            if (error && error.response && (error.response.status === 400 || error.response.status === 500)) {
                modalService.showError(error);
            }
            this._lastError = { message: error.message, time: currentTime };
        }
    }
}

const errorHandleService = new ErrorHandleService();